import merge from 'lodash/merge';
import reduce from 'lodash/reduce';

import { sanitizeEntity } from './sanitize';

/**
 * Combine the given relationships objects
 *
 * See: http://jsonapi.org/format/#document-resource-object-relationships
 * @param {object} oldRels - The old relationships.
 * @param {object} newRels - The new relationships.
 * @returns {object} - The combined relationships.
 */
export const combinedRelationships = (oldRels, newRels) => {
  if (!oldRels && !newRels) {
    // Special case to avoid adding an empty relationships object when
    // none of the resource objects had any relationships.
    return null;
  }
  return { ...oldRels, ...newRels };
};

/**
 * Combine relationships for a sparse update: existing relationship keys win,
 * keys the old resource does not have yet are added.
 * @param {object} oldRels - The old relationships.
 * @param {object} newRels - The new (sparse) relationships.
 * @returns {object|null} - The combined relationships.
 */
const sparseCombinedRelationships = (oldRels, newRels) =>
  oldRels || newRels ? { ...newRels, ...oldRels } : null;

/**
 * True for object literals only, never for SDK class instances (UUID, LatLng,
 * Money), Dates or arrays — those are atomic values and get replaced.
 * @param {*} value
 * @returns {boolean}
 */
const isPlainObject = (value) => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Copy the defined values of source onto target. `undefined` never arrives
 * over the wire; sanitizeEntity emits it for fields the query did not fetch
 * (e.g. title/description, profile.bio), so here it means "not in the response".
 * @param {object} target
 * @param {object} source
 * @param {(oldValue: *, newValue: *) => *} [combine] - Value combiner.
 * @returns {object} target
 */
const assignDefined = (target, source, combine = (oldValue, newValue) => newValue) => {
  Object.keys(source).forEach((key) => {
    if (source[key] !== undefined) target[key] = combine(target[key], source[key]);
  });
  return target;
};

const combineSparseValue = (oldValue, newValue) =>
  isPlainObject(oldValue) && isPlainObject(newValue)
    ? assignDefined({ ...oldValue }, newValue)
    : newValue;

/**
 * Merge the attributes of a sparse response (e.g. a listing-card query with a
 * restricted fieldset) into the existing ones: nested plain objects such as
 * publicData, metadata or image variants are merged one level deep instead of
 * being replaced, so the fields the sparse query did not ask for survive.
 * @param {object} oldAttrs - The old attributes.
 * @param {object} newAttrs - The new (sparse) attributes.
 * @returns {object} - The merged attributes.
 */
const sparseMergedAttributes = (oldAttrs, newAttrs) =>
  assignDefined({ ...oldAttrs }, newAttrs, combineSparseValue);

const replaceMergedAttributes = (oldAttrs, newAttrs) => ({ ...oldAttrs, ...newAttrs });

// How an existing resource absorbs a new version of itself: a full response
// replaces top-level attributes and relationship keys, a sparse one merges.
const REPLACE_MERGE = {
  mergeAttributes: replaceMergedAttributes,
  mergeRelationships: combinedRelationships,
};
const SPARSE_MERGE = {
  mergeAttributes: sparseMergedAttributes,
  mergeRelationships: sparseCombinedRelationships,
};

const assertSameResource = (oldRes, newRes) => {
  if (newRes.id.uuid !== oldRes.id.uuid || newRes.type !== oldRes.type) {
    throw new Error('Cannot merge resource objects with different ids or types');
  }
};

/**
 * Combine the given resource objects
 *
 * See: http://jsonapi.org/format/#document-resource-objects
 * @param {object} oldRes - The old resource.
 * @param {object} newRes - The new resource.
 * @param {{ sparse?: boolean }} [options] - `sparse`: newRes comes from a query
 *   with a restricted fieldset and must not erase what a full fetch stored.
 * @returns {object} - The combined resource.
 */
export const combinedResourceObjects = (oldRes, newRes, { sparse = false } = {}) => {
  assertSameResource(oldRes, newRes);
  const { id, type } = oldRes;
  const attributes = newRes.attributes || oldRes.attributes;
  const attributesOld = oldRes.attributes || {};
  const attributesNew = newRes.attributes || {};
  const { mergeAttributes, mergeRelationships } = sparse ? SPARSE_MERGE : REPLACE_MERGE;
  // Allow (potentially) sparse attributes to update only relevant fields
  const attrs = attributes ? { attributes: mergeAttributes(attributesOld, attributesNew) } : null;
  const relationships = mergeRelationships(oldRes.relationships, newRes.relationships);
  const rels = relationships ? { relationships } : null;
  return { id, type, ...attrs, ...rels };
};

/**
 * Combine the resource objects form the given api response to the
 * existing entities.
 * @param {object} oldEntities - The old entities.
 * @param {object} apiResponse - The api response.
 * @param {{ sparse?: boolean }} [options] - `sparse`: the response comes from a
 *   restricted-fieldset query; existing entities are merged, not replaced
 *   (see combinedResourceObjects). New entities are inserted as they are.
 * @returns {object} - The updated entities.
 */
export const updatedEntities = (oldEntities, apiResponse, { sparse = false } = {}) => {
  const { data, included = [] } = apiResponse;
  const objects = (Array.isArray(data) ? data : [data]).concat(included);

  return objects.reduce((entities, curr) => {
    const { id, type } = curr;

    // Some entities (e.g. listing and user) might include extended data,
    // you should check if src/util/sanitize.js needs to be updated.
    const current = sanitizeEntity(curr);

    entities[type] = entities[type] || {};
    const entity = entities[type][id.uuid];
    entities[type][id.uuid] = entity
      ? combinedResourceObjects({ ...entity }, current, { sparse })
      : current;

    return entities;
  }, oldEntities);
};

export const updateStoryblokEntities = (oldEntities, storyblokResponse) => {
  const { data } = storyblokResponse;
  const { story, stories, rels } = data;
  let newEntities = story ? [story] : stories;
  newEntities = rels ? newEntities.concat(rels) : newEntities;

  return newEntities.reduce((entities, newEntity) => {
    const { full_slug, content } = newEntity;
    const { component } = content;
    const slug = full_slug.replace(/\/$/, '');
    if (!entities[component]) entities[component] = {};
    if (!entities[component][slug]) entities[component][slug] = {};
    if (!entities[component][slug]) entities[component][slug] = {};
    entities[component][slug] = newEntity;
    return entities;
  }, oldEntities);
};

export const updateTranslationEntities = (oldEntities, translationResponse) => {
  const { data } = translationResponse;
  const objects = Array.isArray(data) ? data : [data];
  return objects.reduce((entities, curr) => {
    const { uuid, type, defaultLocale, translations } = curr;
    entities[type] = entities[type] || {};
    entities[type][uuid] = { defaultLocale, ...translations };
    return entities;
  }, oldEntities);
};

/**
 * Denormalise the entities with the resources from the entities object
 *
 * This function calculates the dernormalised tree structure from the
 * normalised entities object with all the relationships joined in.
 *
 * @param {object} entities entities object in the SDK Redux store
 * @param {Array<{ id: { uuid: string }, type: string }>} resources array of objects
 * with id and type
 * @param {boolean} throwIfNotFound wheather to skip a resource that
 * is not found (false), or to throw an Error (true)
 *
 * @returns {Array} the given resource objects denormalised that were
 * found in the entities
 */
export const denormalisedEntities = (entities, resources, throwIfNotFound = true) => {
  const denormalised = resources.map((res) => {
    const { id, type } = res;
    const entityFound = entities[type] && id && entities[type][id.uuid];
    if (!entityFound) {
      if (throwIfNotFound) {
        throw new Error(`Entity with type "${type}" and id "${id ? id.uuid : id}" not found`);
      }
      return null;
    }
    const entity = entities[type][id.uuid];
    const { relationships, ...entityData } = entity;

    if (relationships) {
      // Recursively join in all the relationship entities
      return reduce(
        relationships,
        (ent, relRef, relName) => {
          // A relationship reference can be either a single object or
          // an array of objects. We want to keep that form in the final
          // result.
          const hasMultipleRefs = Array.isArray(relRef.data);
          const multipleRefsEmpty = hasMultipleRefs && relRef.data.length === 0;
          if (!relRef.data || multipleRefsEmpty) {
            ent[relName] = hasMultipleRefs ? [] : null;
          } else {
            const refs = hasMultipleRefs ? relRef.data : [relRef.data];

            // If a relationship is not found, an Error should be thrown
            const rels = denormalisedEntities(entities, refs, true);

            ent[relName] = hasMultipleRefs ? rels : rels[0];
          }
          return ent;
        },
        entityData
      );
    }
    return entityData;
  });
  return denormalised.filter((e) => !!e);
};

/**
 * Denormalise the data from the given SDK response
 *
 * @param {object} sdkResponse response object from an SDK call
 *
 * @returns {Array} entities in the response with relationships
 * denormalised from the included data
 */
export const denormalisedResponseEntities = (sdkResponse) => {
  const apiResponse = sdkResponse.data;
  const data = apiResponse.data;
  const resources = Array.isArray(data) ? data : [data];

  if (!data || resources.length === 0) {
    return [];
  }

  const entities = updatedEntities({}, apiResponse);
  return denormalisedEntities(entities, resources);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} transaction entity object, which is to be ensured against null values
 * @param {object | null} booking booking object
 * @param {object | null} listing listing object
 * @param {object | null} provider provider object
 * @returns {object} - The transaction object
 */
export const ensureTransaction = (transaction, booking = null, listing = null, provider = null) => {
  const empty = {
    id: null,
    type: 'transaction',
    attributes: {
      protectedData: {},
    },
    booking,
    listing,
    provider,
  };
  return { ...empty, ...transaction };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} booking entity object, which is to be ensured against null values
 * @returns {object} - The booking object
 */
export const ensureBooking = (booking) => {
  const empty = { id: null, type: 'booking', attributes: {} };
  return { ...empty, ...booking };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} listing entity object, which is to be ensured against null values
 * @returns {object} - The listing object
 */
export const ensureListing = (listing) => {
  const empty = {
    id: null,
    type: 'listing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} listing entity object, which is to be ensured against null values
 * @returns {object} - The own listing object
 */
export const ensureOwnListing = (listing) => {
  const empty = {
    id: null,
    type: 'ownListing',
    attributes: { publicData: {} },
    images: [],
  };
  return { ...empty, ...listing };
};

/**
 * Check if a listing's publicData describes a campingGround. For components that
 * only receive `publicData` rather than the whole entity.
 * @param {object} publicData - A listing's `attributes.publicData`
 * @returns {boolean}
 */
export const isCampingGroundPublicData = (publicData) =>
  publicData?.listingCategory === 'campingGround';

/**
 * Check if a listing is a campingGround listing.
 * @param {object} listing - A listing or ownListing entity
 * @returns {boolean}
 */
export const isCampingGroundListing = (listing) =>
  isCampingGroundPublicData(listing?.attributes?.publicData);

/**
 * Coerce a pitch count to the integer that both `publicData.amountOfSeats` and
 * the availability plan's `seats` must hold.
 *
 * Flex indexes `amountOfSeats` as a `long`, so a string like `"3"` is invisible
 * to the search filter, and `seats` is rejected outright unless it is a number.
 * Every writer therefore runs its value through here. Anything that is not a
 * usable count (empty field, NaN, a negative number) falls back to 1 — one
 * bookable pitch is the safe floor, and it matches the form's own minimum.
 *
 * @param {unknown} value - Raw value from a form, an API payload or stored data
 * @returns {number} A positive integer
 */
export const toAmountOfSeats = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * Normalize a value that used to be stored as a scalar (legacy single-select) to an array.
 * Nullish and empty-string values become an empty array.
 *
 * @template T
 * @param {T | T[] | null | undefined} value
 * @returns {T[]}
 */
export const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

export const LISTING_CATEGORIES = ['campingPitch', 'campingGround'];

/**
 * The listing's kind. Falls back to the value carried in the wizard's `category`
 * query param while the draft does not exist yet, then to a pitch.
 *
 * @param {object} listing - A listing or ownListing entity
 * @param {string} [fallback] - Category from the URL, used only when the listing has none
 * @returns {'campingPitch' | 'campingGround'}
 */
export const listingCategoryOf = (listing, fallback) => {
  const fromListing = listing?.attributes?.publicData?.listingCategory;
  if (LISTING_CATEGORIES.includes(fromListing)) return fromListing;
  if (LISTING_CATEGORIES.includes(fallback)) return fallback;
  return 'campingPitch';
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} user entity object, which is to be ensured against null values
 * @returns {object} - The user object
 */
export const ensureUser = (user) => {
  const empty = { id: null, type: 'user', attributes: { profile: {} } };
  return { ...empty, ...user };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} user user entity object, which is to be ensured against null values
 * @returns {object} - The current user object
 */
export const ensureCurrentUser = (user) => {
  const empty = {
    id: null,
    type: 'currentUser',
    stripeCustomer: {
      defaultPaymentMethod: {
        attributes: {},
      },
    },
    attributes: {
      profile: {
        privateData: {
          stripeCustomer: {
            invoice_settings: {},
          },
          paymentMethod: {},
        },
      },
    },
    profileImage: {},
  };
  return merge(empty, user);
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} timeSlot entity object, which is to be ensured against null values
 * @returns {object} - The time slot object
 */
export const ensureTimeSlot = (timeSlot) => {
  const empty = { id: null, type: 'timeSlot', attributes: {} };
  return { ...empty, ...timeSlot };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} availabilityPlan exception entity object, which is to be ensured against null values
 * @returns {object} - The day availability plan object
 */
export const ensureDayAvailabilityPlan = (availabilityPlan) => {
  const empty = { type: 'availability-plan/day', entries: [] };
  return { ...empty, ...availabilityPlan };
};

/**
 * Create shell objects to ensure that attributes etc. exists.
 *
 * @param {object} availabilityException exception entity object, which is to be ensured against null values
 * @returns {object} - The availability exception object
 */
export const ensureAvailabilityException = (availabilityException) => {
  const empty = { id: null, type: 'availabilityException', attributes: {} };
  return { ...empty, ...availabilityException };
};

/**
 * Get the display name of the given user as string. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned or deleted users, a translated name should be provided.
 *
 * @param {import('@vansite/ts-sharetribe-flex-sdk').User} user user object
 * @param {string} defaultUserDisplayName default user display name
 *
 * @returns {string} display name that can be rendered in the UI
 */
export const userDisplayNameAsString = (user, defaultUserDisplayName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.displayName;

  if (hasDisplayName) {
    return user.attributes.profile.displayName;
  } else {
    return defaultUserDisplayName || '';
  }
};

/**
 * Get the abbreviated name of the given user. This function handles
 * missing data (e.g. when the user object is still being downloaded),
 * fully loaded users, as well as banned users.
 *
 * For banned  or deleted users, a default abbreviated name should be provided.
 *
 * @param {import('@vansite/ts-sharetribe-flex-sdk').User} user user object
 * @param {string} defaultUserAbbreviatedName default user abbreviated name
 *
 * @returns {string} abbreviated name that can be rendered in the UI
 * (e.g. in Avatar initials)
 */
export const userAbbreviatedName = (user, defaultUserAbbreviatedName) => {
  const hasAttributes = user && user.attributes;
  const hasProfile = hasAttributes && user.attributes.profile;
  const hasDisplayName = hasProfile && user.attributes.profile.abbreviatedName;

  if (hasDisplayName) {
    return user.attributes.profile.abbreviatedName;
  } else {
    return defaultUserAbbreviatedName || '';
  }
};

/**
 * Humanizes a line item code. Strips the "line-item/" namespace
 * definition from the beginnign, replaces dashes with spaces and
 * capitalizes the first character.
 *
 * @param {string} code a line item code
 *
 * @returns {string} returns the line item code humanized
 */
export const humanizeLineItemCode = (code) => {
  if (!/^line-item\/.+/.test(code)) {
    throw new Error(`Invalid line item code: ${code}`);
  }
  const lowercase = code.replace(/^line-item\//, '').replace(/-/g, ' ');

  return lowercase.charAt(0).toUpperCase() + lowercase.slice(1);
};
